import React, { useEffect, useState, useMemo, useRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Autocomplete,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
  Grow,
  Tooltip,
  useTheme
} from '@mui/material';
import { VolumeUp, AutoAwesomeOutlined } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { addInfo, addError } from 'app/actions/snackbar/snackbar';
import {
  usePersonaUpdatePersona,
  usePersonaCreatePersona,
  useAvatarGetAllAvatars,
  getPersonaGetPersonaQueryKey,
  getPersonaGetPersonasQueryKey
} from '@/api-gen/api';
import {
  PersonaDto,
  UpdatePersonaDto,
  CreatePersonaDto,
  Gender,
  TtsProvider
} from '@/api-gen/api.schemas';
import { FormAvatarPicker } from '@/app/components/AvatarPicker/FormAvatarPicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { voicesQuery } from 'app/queries/speechQueries';
import { alpha } from '@mui/material/styles';
import type { SynthesizeSpeechCommand, GeneratePersonaSentenceRequestDto } from '@/api-gen/api.schemas';
import { speechSynthesizeSpeech, personaGeneratePersonaSentence } from '@/api-gen/api';
import { TtsProviderLogo } from '@/app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/TtsProviderLogo';
import { LanguageFlag } from '@/app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/LanguageFlag';
import { CurrentUserSelector } from 'app/selectors/userSelectors';
import { ScenarioSettingsLanguageVals } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/types';
import LanguageSelector from 'app/components/form/LanguageSelector';
import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';
import GenderSelector from './GenderSelector';
import PersonaChatPanel from './PersonaChatPanel';
import {
  resolveSelectedVoice,
  mapPersonaDtoToForm,
  mapFormToCreatePersonaDto,
  mapFormToUpdatePersonaDto,
  resolveVoiceNameForTTS
} from 'app/utils/voiceHelpers';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';
import {
  selectDefaultAvatarWithFallback,
  getVariedAvatarSelection,
  FALLBACK_AVATARS
} from 'app/utils/avatarSelectionUtils';
import { usePersonaChat } from 'app/hooks/usePersonaChat.ts';
import AIRewriteDialog from './AIRewrite/AIRewriteDialog';
import { useAIRewrite } from './AIRewrite/useAIRewrite';

const personaSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().optional().nullable(),
  languageCode: z.string().min(2).max(10),
  gender: z.nativeEnum(Gender),
  ttsProvider: z.nativeEnum(TtsProvider),
  voiceName: z.string().min(1).max(100),
  voiceId: z.string().optional().nullable(),
  avatarUrl: z.string().max(500).optional().nullable().or(z.literal(''))
});

type PersonaFormData = z.infer<typeof personaSchema>;

// Voice option type for Autocomplete
interface VoiceOption {
  id: string | null;
  name: string;
  sex: Gender;
  languageCode: string;
  label: string;
  ttsProvider: TtsProvider;
}

interface PersonaFormProps {
  persona?: PersonaDto;
  onSave: (createdPersona?: PersonaDto) => void;
  onCancel: () => void;
  mode?: 'create' | 'edit';
  defaultLanguageCode?: string;
  readOnly?: boolean;
}

// Imperative handle interface for programmatic form submission
interface PersonaFormHandle {
  submit: () => void;
  isDirty: boolean;
  startPersonaChat: () => void;
  stopPersonaChat: () => void;
  closePersonaChat: () => void;
}

const PersonaForm = React.forwardRef<PersonaFormHandle, PersonaFormProps>(
  ({ persona, onSave, onCancel, mode = 'edit', defaultLanguageCode, readOnly = false }, ref) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const currentUser = useSelector(CurrentUserSelector);
    const companyId = currentUser?.companyId;

    const { data: avatarsData } = useAvatarGetAllAvatars();
    const avatars = avatarsData?.data || [];
    const { data: voicesData } = useQuery(voicesQuery);

    // State for audio playback
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const { t: translate } = useTranslation(I18nNamespace.Personas);
    const { t: translateCommon } = useTranslation(I18nNamespace.Common);

    // Create validation schema with translated error messages
    const personaSchemaWithTranslations = React.useMemo(
      () =>
        z.object({
          name: z
            .string()
            .min(1, translate(nameof.full<I18nPersonasNs>((n) => n.validation.nameRequired)))
            .max(100, translate(nameof.full<I18nPersonasNs>((n) => n.validation.nameMaxLength))),
          description: z.string().optional().nullable(),
          languageCode: z
            .string()
            .min(2, translate(nameof.full<I18nPersonasNs>((n) => n.validation.languageRequired)))
            .max(10, translate(nameof.full<I18nPersonasNs>((n) => n.validation.languageMaxLength))),
          gender: z.nativeEnum(Gender),
          ttsProvider: z.nativeEnum(TtsProvider),
          voiceName: z
            .string()
            .min(1, translate(nameof.full<I18nPersonasNs>((n) => n.validation.voiceNameRequired)))
            .max(100, translate(nameof.full<I18nPersonasNs>((n) => n.validation.voiceNameMaxLength))),
          voiceId: z.string().optional().nullable(),
          avatarUrl: z
            .string()
            .max(500, translate(nameof.full<I18nPersonasNs>((n) => n.validation.avatarUrlMaxLength)))
            .optional()
            .nullable()
            .or(z.literal(''))
        }),
      [translate]
    );

    // State for tracking recently used avatars to provide variety
    const [recentlyUsedAvatars, setRecentlyUsedAvatars] = useState<string[]>([]);

    const {
      control,
      handleSubmit,
      formState: { errors, isDirty },
      reset,
      watch,
      setValue,
      getValues
    } = useForm<PersonaFormData>({
      resolver: zodResolver(personaSchemaWithTranslations),
      defaultValues: {
        name: persona?.name || '',
        description: persona?.description || '',
        languageCode: persona?.languageCode || defaultLanguageCode || ScenarioSettingsLanguageVals.English,
        gender: persona?.gender || Gender.Female,
        ttsProvider: persona?.ttsProvider || TtsProvider.ElevenLabs,
        voiceName: persona?.voiceName || '',
        voiceId: persona?.voiceId || null,
        avatarUrl:
          persona?.avatarUrl ||
          (mode === 'create'
            ? selectDefaultAvatarWithFallback(avatars, persona?.gender || Gender.Female, recentlyUsedAvatars)
            : '')
      }
    });

    const theme = useTheme();
    const watchedProvider = watch('ttsProvider');
    const watchedLanguage = watch('languageCode');
    const watchedGender = watch('gender');
    const watchedName = watch('name');
    const watchedDescription = watch('description');
    const watchedVoiceId = watch('voiceId');
    const watchedAvatar = watch('avatarUrl');

    const personaChat = usePersonaChat({
      gender: watchedGender,
      name: watchedName,
      languageCode: watchedLanguage,
      ttsProvider: watchedProvider,
      voiceId: watchedVoiceId ?? null,
      description: watchedDescription
    });

    const aiRewrite = useAIRewrite((rewrittenText: string) => {
      setValue('description', rewrittenText, { shouldValidate: true, shouldDirty: true });
    });

    const {
      startChat: startPersonaChat,
      stopChat: stopPersonaChat,
      closeChat: closePersonaChat,
      isOpen: isPersonaChatOpen,
      state: personaChatStatus,
      isRecording: isPersonaChatRecording,
      isLoading: isPersonaChatLoading,
      isResponding: isPersonaChatResponding,
      error: personaChatErrorMessage
    } = personaChat;

    // Filter voices based on company, provider, language and gender (matching ScenarioGeneralSettings logic)
    const voiceOptions = useMemo((): VoiceOption[] => {
      if (!voicesData) return [];

      // First filter by company if companyId exists
      const companyFilteredVoices = companyId
        ? voicesData.filter((v) => {
            // @ts-ignore companyId may not exist on all voice options
            const voiceCompanyId = v.companyId;
            return !voiceCompanyId || voiceCompanyId === companyId;
          })
        : voicesData;

      return companyFilteredVoices
        .map((voice) => ({
          id: voice.id || null,
          name: voice.name || '',
          sex: (voice.sex || Gender.Female) as Gender,
          languageCode: voice.languageCode || '',
          ttsProvider: (voice.ttsProvider || TtsProvider.InHouse) as TtsProvider,
          label: `${voice.name} (${
            voice.sex === Gender.Male
              ? translate(nameof.full<I18nPersonasNs>((n) => n.male))
              : translate(nameof.full<I18nPersonasNs>((n) => n.female))
          })`
        }))
        .filter((voice) => {
          if (!voice.name) return false;
          if (watchedProvider && voice.ttsProvider !== watchedProvider) return false;
          // Filter by selected gender
          if (watchedGender && voice.sex !== watchedGender) return false;
          // For ElevenLabs, show all voices regardless of language
          if (watchedProvider === TtsProvider.ElevenLabs) return true;
          // For other providers, filter by language
          if (watchedLanguage && voice.languageCode !== watchedLanguage) return false;
          return true;
        })
        .sort((a, b) => {
          // For ElevenLabs, selected language voices first, then group by language
          if (watchedProvider === TtsProvider.ElevenLabs) {
            // Selected language voices first
            if (a.languageCode === watchedLanguage && b.languageCode !== watchedLanguage) return -1;
            if (b.languageCode === watchedLanguage && a.languageCode !== watchedLanguage) return 1;

            // For non-selected languages, group by language first
            if (a.languageCode !== watchedLanguage && b.languageCode !== watchedLanguage) {
              const langCompare = (a.languageCode || '').localeCompare(b.languageCode || '');
              if (langCompare !== 0) return langCompare;
            }
          }

          // Within same language, sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
    }, [voicesData, watchedProvider, watchedLanguage, watchedGender, companyId, translate]);

    // Clear voiceName if current selection is no longer valid after filtering
    useEffect(() => {
      const currentVoiceName = getValues('voiceName');
      if (!currentVoiceName) return;
      const stillValid = voiceOptions.some((opt) => opt.name === currentVoiceName && opt.sex === watchedGender);
      if (!stillValid) {
        setValue('voiceName', '');
      }
    }, [voiceOptions, watchedGender, getValues, setValue]);

    // Create audio element for playback
    useEffect(() => {
      const audio = new Audio();
      const handleEnded = () => {
        setIsPlayingAudio(false);
      };

      audio.addEventListener('ended', handleEnded);
      audioRef.current = audio;

      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.pause();
        audio.src = '';
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
      };
    }, []);

    // Setup speech synthesis mutation
    const synthesizeSpeechMutation = useMutation<string, unknown, SynthesizeSpeechCommand>({
      mutationFn: async (command: SynthesizeSpeechCommand) => {
        const { data } = await speechSynthesizeSpeech(command as any);
        return `data:audio/mpeg;base64, ${data}`;
      },
      onSuccess: (audioUrl) => {
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play().catch((err) => {
            console.error('Error playing audio:', err);
            setIsPlayingAudio(false);
          });
          return;
        }
        setIsPlayingAudio(false);
      },
      onError: (error) => {
        console.error('Speech synthesis error:', error);
        setIsPlayingAudio(false);
        dispatch(addError({ errorText: 'Failed to generate voice preview' }));
      }
    });

    // Legacy enum converters removed — Orval enums used directly

    // Handle speaker icon click
    const handleSpeakerClick = async (event: React.MouseEvent) => {
      event.stopPropagation();

      if (readOnly || isPlayingAudio || synthesizeSpeechMutation.isLoading) return;

      const currentValues = getValues();
      const { gender, voiceName, voiceId, ttsProvider, languageCode, description, name } =
        currentValues as PersonaFormData & { name: string };

      // Resolve voice name from voiceId, fallback to voiceName
      const resolvedVoiceName = resolveVoiceNameForTTS(voicesData || [], voiceId, voiceName);

      if (!resolvedVoiceName) return;

      setIsPlayingAudio(true);

      try {
        const request: GeneratePersonaSentenceRequestDto = {
          personaDescription:
            description && description.trim().length > 0
              ? description
              : name && name.trim().length > 0
              ? name
              : 'Persona',
          language: languageCode
        };

        const { data: sentenceResponse } = await personaGeneratePersonaSentence(request);
        const generatedText =
          sentenceResponse?.sentence && sentenceResponse.sentence.trim().length > 0 ? sentenceResponse.sentence : '';

        if (!generatedText) {
          throw new Error('No sentence generated');
        }

        const command: SynthesizeSpeechCommand = {
          voiceName: resolvedVoiceName,
          voiceId,
          sex: gender,
          service: ttsProvider,
          language: languageCode,
          text: generatedText,
          speed: 1.0
        };

        synthesizeSpeechMutation.mutate(command);
      } catch (err) {
        console.error('Error generating persona sentence:', err);
        setIsPlayingAudio(false);
        dispatch(addError({ errorText: 'Failed to generate test sentence' }));
      }
    };

    const updateMutation = usePersonaUpdatePersona({
      mutation: {
        onSuccess: (data, variables) => {
          // Invalidate the specific persona query to ensure fresh data
          queryClient.invalidateQueries({ queryKey: getPersonaGetPersonaQueryKey(variables.id) });
          // Invalidate the personas list query to refresh all consumers (PersonasManager, etc.)
          queryClient.invalidateQueries({ queryKey: getPersonaGetPersonasQueryKey() });
          dispatch(
            addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.personaUpdatedSuccess)) })
          );
          // Pass updated persona to parent for optimistic UI updates where relevant
          const updatedPersona = (data as any)?.data ?? data;
          onSave(updatedPersona as PersonaDto);
        },
        onError: (error: any) => {
          dispatch(
            addError({
              errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.updatePersonaError))
            })
          );
        }
      }
    });

    const createMutation = usePersonaCreatePersona({
      mutation: {
        onSuccess: (response) => {
          dispatch(
            addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.personaCreatedSuccess)) })
          );
          // Pass the created persona data back
          const createdPersona = response?.data || response;

          onSave(createdPersona as PersonaDto);
        },
        onError: (error: any) => {
          dispatch(
            addError({
              errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.createPersonaError))
            })
          );
        }
      }
    });

    // Track previous gender to detect changes
    const prevGenderRef = useRef<Gender | undefined>(undefined);

    // Reset avatar to default when gender changes
    useEffect(() => {
      if (prevGenderRef.current !== undefined && prevGenderRef.current !== watchedGender) {
        // Gender changed, select a varied avatar for new gender
        const { avatarUrl, updatedRecent } = getVariedAvatarSelection(avatars, watchedGender, recentlyUsedAvatars);
        const finalAvatarUrl = avatarUrl || FALLBACK_AVATARS[watchedGender] || FALLBACK_AVATARS[Gender.Female];

        setValue('avatarUrl', finalAvatarUrl);
        setRecentlyUsedAvatars(updatedRecent);
      }
      prevGenderRef.current = watchedGender;
    }, [watchedGender, setValue, avatars, recentlyUsedAvatars]);

    // Initialize avatar selection for create mode when avatars are loaded
    useEffect(() => {
      if (mode === 'create' && avatars.length > 0 && !persona) {
        const currentAvatarUrl = getValues('avatarUrl');

        // If no avatar is set or it's a fallback URL, select a better one
        if (!currentAvatarUrl || Object.values(FALLBACK_AVATARS).includes(currentAvatarUrl)) {
          const currentGender = getValues('gender');
          const { avatarUrl, updatedRecent } = getVariedAvatarSelection(avatars, currentGender, recentlyUsedAvatars);
          const finalAvatarUrl = avatarUrl || FALLBACK_AVATARS[currentGender] || FALLBACK_AVATARS[Gender.Female];

          setValue('avatarUrl', finalAvatarUrl);
          setRecentlyUsedAvatars(updatedRecent);
        }
      }
    }, [mode, avatars, persona, recentlyUsedAvatars, getValues, setValue]);

    useEffect(() => {
      if (persona) {
        reset({
          name: persona.name || '',
          description: persona.description || '',
          languageCode: persona.languageCode || defaultLanguageCode || ScenarioSettingsLanguageVals.English,
          gender: persona.gender || Gender.Female,
          ttsProvider: persona.ttsProvider || TtsProvider.ElevenLabs,
          voiceName: persona.voiceName || '',
          // Ensure the Autocomplete binds to the correct saved voice after reopen
          // even though voiceId is not part of the validation schema

          voiceId: persona.voiceId || null,
          avatarUrl: persona.avatarUrl || ''
        });
        // Update the ref when persona data is loaded
        prevGenderRef.current = persona.gender || Gender.Female;
      }
    }, [persona, reset, defaultLanguageCode]);

    const onSubmit = (data: PersonaFormData, event?: React.BaseSyntheticEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (readOnly) {
        return;
      }
      if (mode === 'create') {
        const createData = mapFormToCreatePersonaDto(data, voicesData || []);

        createMutation.mutate({ data: createData as CreatePersonaDto });
      } else if (persona?.id) {
        const updateData = mapFormToUpdatePersonaDto({ ...data, id: persona.id }, voicesData || []);

        updateMutation.mutate({
          id: persona.id,
          data: updateData as UpdatePersonaDto
        });
      }
    };

    // Expose submit function for programmatic submission (avoids native DOM events)
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)(); // Direct React Hook Form submission
      },
      isDirty,
      startPersonaChat: () => {
        startPersonaChat();
      },
      stopPersonaChat: () => {
        stopPersonaChat();
      },
      closePersonaChat: () => {
        closePersonaChat();
      }
    }));

    return (
      <form
        id={mode === 'create' ? 'persona-create-form' : 'persona-edit-form'}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box
              sx={{
                position: 'relative',
                margin: '0 auto',
                width: isPersonaChatOpen ? '100%' : 200,
                height: isPersonaChatOpen ? 420 : 200,
                transition: theme.transitions.create(['width', 'height', 'padding', 'background-color', 'box-shadow'], {
                  duration: theme.transitions.duration.standard,
                  easing: theme.transitions.easing.easeInOut
                }),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // padding: isPersonaChatOpen ? theme.spacing(3) : 0,
                // backgroundColor: isPersonaChatOpen ? alpha(theme.palette.primary.light, 0.08) : 'transparent',
                // borderRadius: isPersonaChatOpen ? 24 : theme.shape.borderRadius,
                // boxShadow: isPersonaChatOpen ? theme.shadows[8] : 'none',
                overflow: 'hidden'
              }}
            >
              <Fade in={!isPersonaChatOpen} timeout={{ enter: 200, exit: 200 }} mountOnEnter unmountOnExit>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FormAvatarPicker
                    control={control}
                    name="avatarUrl"
                    avatars={avatars}
                    variant="dialog"
                    previewSize={160}
                    selectedGender={watchedGender}
                    disabled={readOnly}
                    helperText={errors.avatarUrl?.message}
                  />
                </Box>
              </Fade>
              <Fade in={isPersonaChatOpen} timeout={{ enter: 300, exit: 150 }} mountOnEnter unmountOnExit>
                <Grow in={isPersonaChatOpen} timeout={{ enter: 360, exit: 150 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PersonaChatPanel
                      isListening={personaChatStatus === 'listening'}
                      isSpeaking={personaChatStatus === 'speaking'}
                      isRecording={isPersonaChatRecording}
                      isLoading={isPersonaChatLoading}
                      isResponding={isPersonaChatResponding}
                      status={personaChatStatus}
                      error={personaChatErrorMessage}
                      portraitSrc={
                        watchedAvatar && watchedAvatar.trim().length > 0
                          ? getAvatarUrlWithSas(watchedAvatar)
                          : undefined
                      }
                      personaName={watchedName}
                    />
                  </Box>
                </Grow>
              </Fade>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <GenderSelector
                  value={field.value === Gender.Male ? 'male' : field.value === Gender.Female ? 'female' : ''}
                  onChange={(value) => {
                    field.onChange(value === 'male' ? Gender.Male : Gender.Female);
                  }}
                  disabled={readOnly}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate(nameof.full<I18nPersonasNs>((n) => n.name))}
                  fullWidth
                  required
                  disabled={readOnly}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
          </Grid>

          {/* Language and TTS Provider on the same row */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Box sx={{ '& .MuiTextField-root': { margin: 0 } }}>
                  <LanguageSelector
                    name="languageCode"
                    control={control}
                    label={translate(nameof.full<I18nPersonasNs>((n) => n.language))}
                    disabled={readOnly}
                    error={!!errors.languageCode}
                    helperText={errors.languageCode?.message || ''}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  control={control}
                  name="ttsProvider"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      variant="outlined"
                      label={translate(nameof.full<I18nPersonasNs>((n) => n.ttsProvider))}
                      fullWidth
                      required
                      disabled={readOnly}
                      error={!!errors.ttsProvider}
                      helperText={errors.ttsProvider?.message || ''}
                    >
                      <MenuItem key={TtsProvider.ElevenLabs} value={TtsProvider.ElevenLabs}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TtsProviderLogo provider={TtsProvider.ElevenLabs} size="small" />
                          {translate(nameof.full<I18nPersonasNs>((n) => n.ttsProviders.elevenLabs))}
                        </Box>
                      </MenuItem>
                      <MenuItem key={TtsProvider.Google} value={TtsProvider.Google}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TtsProviderLogo provider={TtsProvider.Google} size="small" />
                          {translate(nameof.full<I18nPersonasNs>((n) => n.ttsProviders.google))}
                        </Box>
                      </MenuItem>
                      <MenuItem key={TtsProvider.Microsoft} value={TtsProvider.Microsoft}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TtsProviderLogo provider={TtsProvider.Microsoft} size="small" />
                          {translate(nameof.full<I18nPersonasNs>((n) => n.ttsProviders.microsoft))}
                        </Box>
                      </MenuItem>
                      <MenuItem key={TtsProvider.InHouse} value={TtsProvider.InHouse}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TtsProviderLogo provider={TtsProvider.InHouse} size="small" />
                          {translate(nameof.full<I18nPersonasNs>((n) => n.ttsProviders.inHouse))}
                        </Box>
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Voice selection on its own row */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="voiceId"
              render={({ field }) => {
                // Match selected voice by voiceId; fall back to name only for null-id voices
                const currentVoiceId = field.value;
                const currentVoiceName = watch('voiceName');

                const selectedVoiceOption = currentVoiceId
                  ? voiceOptions.find((option) => option.id === currentVoiceId)
                  : currentVoiceName
                    ? voiceOptions.find((option) => option.id === null && option.name === currentVoiceName)
                    : undefined;

                const selectedVoice = currentVoiceId
                  ? voicesData?.find((voice) => voice.id === currentVoiceId)
                  : currentVoiceName
                    ? voicesData?.find((voice) => voice.id === null && voice.name === currentVoiceName)
                    : undefined;

                return (
                  <Autocomplete
                    {...field}
                    fullWidth
                    disableClearable
                    disabled={readOnly}
                    options={voiceOptions}
                    value={selectedVoiceOption || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setValue('gender', newValue.sex);
                        setValue('voiceName', newValue.name);
                        // Update both Controller field and form store to keep in sync
                        field.onChange(newValue.id);
                      } else {
                        setValue('gender', Gender.Female);
                        setValue('voiceName', '');
                        field.onChange(null);
                      }
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id && option.name === value.name && option.sex === value.sex
                    }
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box
                          key={option.id ?? key}
                          component="li"
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          {...otherProps}
                        >
                          <LanguageFlag languageCode={option.languageCode} size="small" />
                          {option.label}
                        </Box>
                      );
                    }}
                    renderInput={(params) => {
                      let startAdornment = null;
                      if (selectedVoice?.languageCode) {
                        startAdornment = (
                          <InputAdornment position="start" sx={{ ml: 0.5 }}>
                            <LanguageFlag languageCode={selectedVoice.languageCode} size="small" />
                          </InputAdornment>
                        );
                      }

                      let endAdornment = null;
                      if (selectedVoiceOption) {
                        const loading = isPlayingAudio || synthesizeSpeechMutation.isLoading;
                        endAdornment = (
                          <InputAdornment position="end">
                            {loading ? (
                              <CircularProgress size={16} thickness={5} />
                            ) : (
                              <IconButton
                                size="small"
                                onClick={handleSpeakerClick}
                                disabled={readOnly || loading}
                                edge="end"
                              >
                                <VolumeUp fontSize="small" color={readOnly || loading ? 'disabled' : 'action'} />
                              </IconButton>
                            )}
                          </InputAdornment>
                        );
                      }

                      const helperText =
                        errors.voiceName?.message ||
                        (voiceOptions.length === 0
                          ? translate(nameof.full<I18nPersonasNs>((n) => n.form.noVoicesAvailable))
                          : '') ||
                        '';

                      return (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={translate(nameof.full<I18nPersonasNs>((n) => n.voiceName))}
                          required
                          disabled={readOnly}
                          error={!!errors.voiceName}
                          helperText={helperText}
                          placeholder={
                            !selectedVoiceOption ? translate(nameof.full<I18nPersonasNs>((n) => n.form.none)) : ''
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: startAdornment,
                            endAdornment: (
                              <>
                                {endAdornment}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      );
                    }}
                  />
                );
              }}
            />
          </Grid>

          {/* Description moved to bottom */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    {...field}
                    value={field.value || ''}
                    label={translate(nameof.full<I18nPersonasNs>((n) => n.description))}
                    placeholder="Describe your persona (optional)"
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={12}
                    disabled={readOnly}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                  <Tooltip
                    title={
                      readOnly
                        ? translate(nameof.full<I18nPersonasNs>((n) => n.validation.readOnlyTooltip))
                        : translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.buttonTooltip))
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        aiRewrite.openDialog(field.value || '', watchedGender, watchedName, watchedLanguage)
                      }
                      aria-label={translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.buttonAriaLabel))}
                      disabled={aiRewrite.isLoading || readOnly}
                      sx={{
                        position: 'absolute',
                        bottom: theme.spacing(1),
                        right: theme.spacing(1),
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        '&:hover': {
                          backgroundColor: theme.palette.background.paper
                        },
                        zIndex: 1
                      }}
                    >
                      <AutoAwesomeOutlined fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            />
          </Grid>
        </Grid>

        <AIRewriteDialog
          isOpen={aiRewrite.isOpen}
          currentStep={aiRewrite.currentStep}
          originalDescription={aiRewrite.originalDescription}
          customInstructions={aiRewrite.customInstructions}
          rewrittenDescription={aiRewrite.rewrittenDescription}
          isLoading={aiRewrite.isLoading}
          error={aiRewrite.error}
          onInstructionsChange={aiRewrite.setCustomInstructions}
          onSubmit={aiRewrite.submitRewrite}
          onConfirm={aiRewrite.confirmRewrite}
          onBack={aiRewrite.goBackToInstructions}
          onClose={aiRewrite.closeDialog}
          onClearError={aiRewrite.clearError}
        />
      </form>
    );
  }
);

// Add display name for better debugging
PersonaForm.displayName = 'PersonaForm';

export default PersonaForm;
export type { PersonaFormHandle };
